import { useContext, useEffect, useState } from 'react';
import { UserRound } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { useDownloadInBackground } from '@hooks/use-issue';

import styles from './Issues.module.css';

import { Icon } from '../../components/icon/Icon';
import { useBackUrl } from '../../App.hooks';
import { useGetIssues } from './Issues.hooks';
import { Issue, NavbarIconLogo } from '../../constants';
import { Loading } from '../../components/loading/Loading';
import { useTrackingPageView } from '../../tracking/Tracking.hooks';
import config from '../../config';

import { IssuesContext } from './Issues.context';
import { ProfileMenu } from '../../components/menu/Menu';

export const Issues = () => {
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen);
	};
	return (
		<IssuesContext.Provider
			value={{
				isMenuOpen,
				setIsMenuOpen,
				toggleMenu,
			}}
		>
			<div className={styles['container']}>
				<View />
			</div>
			<ProfileMenu open={isMenuOpen} setOpen={setIsMenuOpen} />
		</IssuesContext.Provider>
	);
};

function View() {
	return (
		<div className={styles['view']}>
			<Topbar />
			<Content />
		</div>
	);
}

function Topbar() {
	return (
		<div className={styles['topbar']}>
			<div>
				<BackButton />
			</div>
			<div className={styles['title']}>
				<NavbarIconLogo />
			</div>
			<div>
				<ProfileButton />
			</div>
		</div>
	);
}

function ProfileButton() {
	const { toggleMenu } = useContext(IssuesContext);
	return (
		<button
			type="button"
			className="tooltip down"
			data-text={'Profile'}
			onClick={() => {
				toggleMenu();
			}}
		>
			<UserRound color={'#c3c3c3'} size={24} />
		</button>
	);
}

function BackButton() {
	const url = useBackUrl();
	return (
		<button id="back-button" type="button" title="Back">
			<Link to={url}>
				<Icon id={'back-btn'} name={'arrow-left'} color={'#333'} />
			</Link>
		</button>
	);
}

function Content() {
	const { issues, isLoading } = useGetIssues();
	const location = useLocation();
	useDownloadInBackground(issues);
	useTrackingPageView({
		title: issues.length ? getTitle() : undefined,
		location: location.pathname,
	});
	useEffect(() => {
		if (!issues.length) {
			return;
		}
		document.title = getTitle();
	}, [issues]);
	if (isLoading) {
		return <Loading />;
	}
	return (
		<div className={styles['content']}>
			<div className={styles['container']}>
				{issues.map((i: Issue) => (
					<IssueCover key={i.id} {...i} />
				))}
			</div>
		</div>
	);
}

function getTitle() {
	return [config.build.title, 'Issues'].join(' - ');
}

function IssueCover(issue: Issue) {
	const { id, cover, name, slug } = issue;
	const url = `/preview/${slug ? slug : id}`;
	const from = window.location.pathname;
	return (
		<Link
			to={url}
			state={{ from }}
			id={`story-${id}`}
			className={styles['issue']}
		>
			<figure>
				<img loading="lazy" src={cover} alt={name} />
				<figcaption>{name}</figcaption>
			</figure>
		</Link>
	);
}

export default Issues;
